import { HistoryInfoFragment, HistoryInfoFragmentType } from '@common/graphql';
import { gql, TypedDocumentNode } from '@common/hooks';
import {
  CalendarViewFiltersQueryCgResponse,
  CalendarViewFiltersQueryVariables,
  IndexShiftConfigsQueryCgResponse,
  IndexShiftConfigsQueryVariables,
  ShiftEditButtonQueryCgResponse,
  ShiftEditButtonQueryVariables,
  ShiftGapDetailsQueryCgResponse,
  ShiftGapDetailsQueryVariables,
  ShiftGapsByDayQueryCgResponse,
  ShiftGapsByDayQueryVariables,
  ShiftGapTagsQueryCgResponse,
  ShiftGapTagsQueryVariables,
  ShiftsByDayQueryCgResponse,
  ShiftsByDayQueryVariables,
  ShiftsCountByDayQueryCgResponse,
  ShiftsCountByDayQueryVariables,
  WorkforceGroupMatchBreakdownQueryCgResponse,
  WorkforceGroupMatchBreakdownQueryVariables,
} from '@common/graphql/schema';

export const LOCALSTORAGE_SHIFT_CALENDAR_FILTER_KEY = 'CalendarViewFilters';

export type ShiftType = {
  id: string;
  shiftDuration: number;
  timeOfDay: string;
  startDatetime: string;
  endDatetime: string;
  timeZone: string;
  state: string;
  readOnly: boolean;
  openShiftGapOpeningsCount: number;
  role: {
    id: string;
    name: string;
  };
  organization?: {
    id: string;
  };
  organizationUnit: {
    id: string;
    name: string;
    costCenter: string;
  };
  organizationBusiness: {
    name: string;
  };
  canAddOpenings: {
    value: boolean;
  };
  canEditProperties: {
    value: boolean;
  };
  shiftGapOpenings: ShiftOpeningType[];
  openShiftGapOpenings: ShiftOpeningType[];
  shiftGapTags?: ShiftGapTagType[];
  history: HistoryInfoFragmentType[];
};

type ShiftOpeningType = {
  id: string;
  state: ShiftOpeningStateType;
  stateChangedAt: string;
  canApproveReject: {
    value: boolean;
  };
  createdBy: {
    name: string;
  };
  canEdit: {
    value: boolean;
  };
  worksShift?: WorksShiftType;
  shiftGap: ShiftType;
};

export type WorksShiftCancelledReasonType = {
  value: string;
  description: string;
};

type WorksShiftType = {
  id: string;
  duration: number;
  perHourIncentivePay: number;
  formattedPerHourIncentivePay: string;
  displayAsLumpSum: boolean;
  formattedLumpSumIncentivePay: string | null;
  incentiveTierLabel: string | null;
  state: WorksShiftStateType;
  shiftGap: {
    organizationUnit: {
      id: string;
    };
  };
  showUnitName: boolean;
  workerNote?: string;
  worker: WorkerType;
  cancelledReason?: string;
  canManagerCancel: {
    value: boolean;
  };
};

type ShiftOpeningStateType =
  | 'open'
  | 'claimed'
  | 'expired'
  | 'cancelled'
  | 'confirmed';

type WorksShiftStateType =
  | 'claimed'
  | 'accepted'
  | 'rejected'
  | 'cancelled'
  | 'confirmed';

export type WorkerType = {
  id: string;
  isInternal: boolean;
  workforceGroup: SimplifiedWorkforceGroupType;
  worksUser: {
    id: string;
    name: string;
  };
  allowLists: AllowListType[];
};

export type AllowListType = {
  staffingRequestAssignment: {
    staffingRequest: {
      billingRegularRate: number;
    };
  };
  organizationUnit: {
    id: string;
  };
  role: {
    id: string;
  };
};

export type OverrideRateOptionType = {
  displayName: string;
  incentiveCents: number;
  moneyAmountCents: number | null;
};

export type MatchBreakdownType = {
  workforceGroupName: string;
  averageRate: number;
  baseRate: number;
  availableResources: number;
  visibleDate: string;
  id: string;
  canEditRate: boolean;
  displayAsLumpSum: boolean;
  formattedLumpSumRate: string | null;
  incentiveTierLabel: string | null;
  overrideRateOptions: OverrideRateOptionType[];
  shiftDuration: number;
  staticPricingTiers: { id: string; label: string; payRate: number }[];
};

export type SimplifiedWorkforceGroupType = {
  id: string;
  name: string;
  hideHomeUnitName?: boolean;
  hideFloatUnitName?: boolean;
};

export const SHIFT_DETAILS_QUERY: TypedDocumentNode<
  ShiftGapDetailsQueryCgResponse,
  ShiftGapDetailsQueryVariables
> = gql`
  query ShiftGapDetails($id: ID!, $sourcePage: String) {
    shiftGap(id: $id, sourcePage: $sourcePage) {
      id
      shiftDuration
      startDatetime
      endDatetime
      timeOfDay
      createdAt
      updatedAt
      timeZone
      state
      source
      readOnly
      openShiftGapOpeningsCount
      fillRate
      shiftDuration
      canAddOpenings {
        value
      }
      canEditProperties {
        value
      }
      history {
        ...HistoryInfoFragment
      }
      organization {
        id
      }
      organizationUnit {
        id
        name
        costCenter
      }
      role {
        id
        name
        abbreviation
      }
      shiftGapOpenings {
        id
        state
        stateChangedAt
        canApproveReject {
          value
        }
        canEdit {
          value
        }
        worksShift {
          id
          duration
          perHourIncentivePay
          displayAsLumpSum
          formattedLumpSumIncentivePay
          incentiveTierLabel
          state
          cancelledReason
          workerNote
          canManagerCancel {
            value
          }
          shiftGap {
            id
            organizationUnit {
              id
            }
          }
          showUnitName
          worker {
            id
            isInternal
            worksUser {
              id
              name
            }
            workforceGroup {
              id
              name
            }
            allowLists {
              id
              organizationUnit {
                id
              }
              staffingRequestAssignment {
                id
                staffingRequest {
                  id
                  billingRegularRate
                }
              }
            }
          }
        }
        createdBy {
          id
          name
        }
      }
      shiftGapTags {
        id
        name
      }
    }
  }
  ${HistoryInfoFragment}
`;

export const INDEX_SHIFT_CONFIGS_QUERY: TypedDocumentNode<
  IndexShiftConfigsQueryCgResponse,
  IndexShiftConfigsQueryVariables
> = gql`
  query IndexShiftConfigs($filters: OrganizationUnitShiftConfigsFilters) {
    organizationUnitShiftConfigs(filters: $filters) {
      id
      startTime
      endTime
      duration
    }
  }
`;

export type MatchBreakdownQueryResponse = {
  workforceGroupMatchBreakdown: MatchBreakdownType[];
};

export const MATCH_BREAKDOWN: TypedDocumentNode<
  WorkforceGroupMatchBreakdownQueryCgResponse,
  WorkforceGroupMatchBreakdownQueryVariables
> = gql`
  query WorkforceGroupMatchBreakdown(
    $organizationUnitId: ID!
    $shiftGapId: ID!
  ) {
    workforceGroupMatchBreakdown(
      organizationUnitId: $organizationUnitId
      shiftGapId: $shiftGapId
    ) {
      workforceGroupName
      availableResources
      averageRate
      baseRate
      canEditRate
      displayAsLumpSum
      formattedLumpSumRate
      incentiveTierLabel
      overrideRateOptions {
        displayName
        incentiveCents
        moneyAmountCents
      }
      shiftDuration
      staticPricingTiers {
        id
        label
        payRate
      }
      visibleDate
      id
    }
  }
`;

export const SHIFT_EDIT_BUTTON: TypedDocumentNode<
  ShiftEditButtonQueryCgResponse,
  ShiftEditButtonQueryVariables
> = gql`
  query ShiftEditButton($id: ID!) {
    shiftGap(id: $id) {
      id
      startDatetime
      timeZone
      state
      canAddOpenings {
        value
      }
      canEditRates {
        value
      }
    }
  }
`;

export type ShiftCountByDayResponse = {
  shiftsCountByDay: [
    {
      date: string;
      openDayShiftsCount: number;
      openNightShiftsCount: number;
      confirmedShiftsCount: number;
    },
  ];
  currentMembership: {
    id: string;
    canCreateShiftGaps: boolean;
    canEditShiftGaps: boolean;
  };
};

export const SHIFTS_COUNT_BY_DAY: TypedDocumentNode<
  ShiftsCountByDayQueryCgResponse,
  ShiftsCountByDayQueryVariables
> = gql`
  query ShiftsCountByDay(
    $month: String!
    $year: Int!
    $filters: ShiftsCountByDayFilters
  ) {
    shiftsCountByDay(month: $month, year: $year, filters: $filters) {
      date
      openDayShiftsCount
      openNightShiftsCount
      confirmedShiftsCount
    }
  }
`;

export type CalendarViewFiltersResponse = {
  organizationUnits: [
    {
      id: string;
      name: number;
      organizationBusiness: {
        id: string;
      };
    },
  ];
  roles: [
    {
      id: string;
      abbreviation: number;
    },
  ];
  organizationBusinesses: [
    {
      id: string;
      name: number;
    },
  ];
};

export const CALENDAR_VIEW_FILTERS_QUERY: TypedDocumentNode<
  CalendarViewFiltersQueryCgResponse,
  CalendarViewFiltersQueryVariables
> = gql`
  query CalendarViewFilters($input: OrganizationUnitsInput) {
    organizationUnits(input: $input) {
      id
      name
      organizationBusiness {
        id
      }
    }
    roles {
      id
      abbreviation
    }
    organizationBusinesses {
      id
      name
    }
  }
`;

export type ShiftByDayOpeningsResponse = {
  id: string;
  state: string;
  shiftGap: ShiftType;
  worksShift: WorksShiftType;
  canApproveReject: {
    value: boolean;
  };
};

export const SHIFTS_BY_DAY: TypedDocumentNode<
  ShiftsByDayQueryCgResponse,
  ShiftsByDayQueryVariables
> = gql`
  query ShiftsByDay(
    $date: ISO8601Date!
    $filters: ShiftsCountByDayFilters
    $offset: Int!
    $limit: Int
    $states: [String!]!
  ) {
    shiftsByDay(
      date: $date
      filters: $filters
      offset: $offset
      limit: $limit
      states: $states
    ) {
      shiftGapOpenings {
        id
        state
        canApproveReject {
          value
        }
        worksShift {
          id
          state
          formattedPerHourIncentivePay
          displayAsLumpSum
          formattedLumpSumIncentivePay
          incentiveTierLabel
          workerNote
          showUnitName
          worker {
            id
            isInternal
            worksUser {
              id
              name
            }
            allowLists {
              id
              organizationUnit {
                id
              }
              role {
                id
              }
              staffingRequestAssignment {
                id
                staffingRequest {
                  id
                  billingRegularRate
                }
              }
            }
            workforceGroup {
              id
              name
            }
          }
        }
        shiftGap {
          id
          timeOfDay
          timeZone
          startDatetime
          endDatetime
          shiftDuration
          source
          role {
            id
            name
            abbreviation
          }
          organizationUnit {
            id
            name
          }
          organizationBusiness {
            id
            name
          }
          shiftGapTags {
            id
            name
          }
        }
      }
      totalCount
    }
  }
`;

export type ShiftGapsByDayResponse = {
  shiftGapsByDay: {
    shiftGaps: [ShiftType];
    totalCount: number;
    totalOpenOpenings: number;
  };
};

export const SHIFT_GAPS_BY_DAY: TypedDocumentNode<
  ShiftGapsByDayQueryCgResponse,
  ShiftGapsByDayQueryVariables
> = gql`
  query ShiftGapsByDay(
    $date: ISO8601Date!
    $filters: ShiftsCountByDayFilters
    $offset: Int!
    $limit: Int
    $states: [String!]!
  ) {
    shiftGapsByDay(
      date: $date
      filters: $filters
      offset: $offset
      limit: $limit
      states: $states
    ) {
      shiftGaps {
        id
        timeOfDay
        timeZone
        startDatetime
        endDatetime
        shiftDuration
        source
        role {
          id
          name
          abbreviation
        }
        organizationUnit {
          id
          name
        }
        organizationBusiness {
          id
          name
        }
        openShiftGapOpenings {
          id
          state
        }
        shiftGapTags {
          id
          name
        }
      }
      totalCount
      totalOpenOpenings
    }
  }
`;

export const SHIFT_GAP_TAGS_QUERY: TypedDocumentNode<
  ShiftGapTagsQueryCgResponse,
  ShiftGapTagsQueryVariables
> = gql`
  query ShiftGapTags($organizationUnitId: ID, $includeAllUnits: Boolean) {
    shiftGapTags(
      organizationUnitId: $organizationUnitId
      includeAllUnits: $includeAllUnits
    ) {
      id
      name
      scope
      organizationUnit {
        id
        name
      }
    }
  }
`;

type ShiftGapTagType = ShiftGapTagsQueryCgResponse['shiftGapTags'][number];
